/**
 * Client of the staff recent-moderation-actions view (M4 item 5, CR-22): GET
 * /api/v1/rooms/{room}/moderation/recent returns the room's last moderation audit rows
 * (newest first) for the web's collapsible «Recent actions» list. Auth is the room-JWT bearer
 * (the join/remint/invites model). Same-origin calls; response bodies are narrowed explicitly
 * (unknown -> typed) instead of cast, mirroring src/api/join.ts.
 */

/** One audit row of the recent-actions view (CR-22); target is null for room-wide actions. */
export interface ModerationActionRow {
  readonly id: number;
  readonly actor: string;
  readonly actorRole: string;
  readonly target: string | null;
  /** The 0005/0011 vocabulary word (mute, kick, promote, ...); rendered localized. */
  readonly action: string;
  /** RFC 3339 creation stamp. */
  readonly at: string;
}

/** Failure of the recent-actions endpoint: HTTP error, network failure or malformed body. */
export class ModerationApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ModerationApiError';
    this.status = status;
    this.code = code;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Narrows one audit row; throws TypeError naming the first violating field. */
export function parseModerationActionRow(value: unknown): ModerationActionRow {
  if (!isRecord(value)) {
    throw new TypeError('moderation action row must be a JSON object');
  }
  const id = value.id;
  if (typeof id !== 'number' || !Number.isInteger(id) || id <= 0) {
    throw new TypeError('moderation action row field "id" must be a positive integer');
  }
  if (typeof value.actor !== 'string' || value.actor.length === 0) {
    throw new TypeError('moderation action row field "actor" must be a non-empty string');
  }
  if (typeof value.actorRole !== 'string' || value.actorRole.length === 0) {
    throw new TypeError('moderation action row field "actorRole" must be a non-empty string');
  }
  if (typeof value.action !== 'string' || value.action.length === 0) {
    throw new TypeError('moderation action row field "action" must be a non-empty string');
  }
  if (typeof value.at !== 'string' || value.at.length === 0) {
    throw new TypeError('moderation action row field "at" must be a timestamp string');
  }
  return {
    id,
    actor: value.actor,
    actorRole: value.actorRole,
    target: typeof value.target === 'string' ? value.target : null,
    action: value.action,
    at: value.at,
  };
}

/** Lists the room's last moderation actions, newest first (default 50, server-capped). */
export async function listRecentModerationActions(
  room: string,
  token: string,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<readonly ModerationActionRow[]> {
  let response: Response;
  try {
    response = await fetchImpl(
      `/api/v1/rooms/${encodeURIComponent(room)}/moderation/recent`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
  } catch {
    throw new ModerationApiError(0, 'network_error', 'control service is unreachable');
  }
  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      // Non-JSON error body, keep the generic message.
    }
    let code = 'unknown_error';
    let message = `recent moderation actions request failed with HTTP ${response.status}`;
    if (isRecord(body) && isRecord(body.error)) {
      if (typeof body.error.code === 'string') {
        code = body.error.code;
      }
      if (typeof body.error.message === 'string') {
        message = body.error.message;
      }
    }
    throw new ModerationApiError(response.status, code, message);
  }
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new ModerationApiError(
      response.status,
      'invalid_response',
      'recent moderation actions response is not JSON',
    );
  }
  if (!isRecord(payload) || !Array.isArray(payload.actions)) {
    throw new ModerationApiError(
      response.status,
      'invalid_response',
      'recent moderation actions response field "actions" must be an array',
    );
  }
  try {
    return payload.actions.map(parseModerationActionRow);
  } catch (cause: unknown) {
    const detail =
      cause instanceof TypeError ? cause.message : 'malformed recent moderation actions response';
    throw new ModerationApiError(response.status, 'invalid_response', detail);
  }
}
